import { useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import type { Event } from '@casetrace/shared';
import { useEventPreview } from '../../hooks/use-event-preview';
import { AttachmentList } from '../case-room/attachment-list';
import { eventAttachments } from '../case-room/event-attachments';
import { TYPE_COLORS, TYPE_ICONS, TYPE_LABELS } from '../case-room/timeline/event-type-config';
import type { StoryHighlight } from './story-model';
import { StoryAttachmentKinds } from './story-attachment-kinds';

interface StoryCardProps {
  readonly item: StoryHighlight;
  readonly index: number;
  readonly isExpanded: boolean;
  readonly onHoverStart: (item: StoryHighlight) => void;
  readonly onToggleExpand: (item: StoryHighlight) => void;
}

const DATE_FORMATTER = new Intl.DateTimeFormat('nb-NO', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'UTC',
});

const PLACEHOLDER_SOURCE = 'story-preview' as Event['source'];
const PLACEHOLDER_ITEM = 'story-preview-item' as Event['sourceItemId'];

export function StoryCard({
  item,
  index,
  isExpanded,
  onHoverStart,
  onToggleExpand,
}: StoryCardProps) {
  const iconColor = TYPE_COLORS[item.highlight.type];
  const Icon = TYPE_ICONS[item.highlight.type];
  const typeLabel = TYPE_LABELS[item.highlight.type];

  const seedEvent = useMemo(() => toSeedEvent(item), [item]);
  const previewEvent = useEventPreview(item.highlight.caseId, seedEvent, true);
  const event = previewEvent ?? seedEvent;
  const previewText = event.contentPreview?.trim() ?? item.highlight.note?.trim() ?? '';
  const attachments = eventAttachments(event);
  const isPlaceholder = event.source === PLACEHOLDER_SOURCE;

  return (
    <article
      data-story-position={item.position}
      data-story-index={index}
      data-story-active={isExpanded ? 'true' : 'false'}
      className={cardClassName(isExpanded)}
      style={{
        transform: 'translate3d(-50%, -50%, 0)',
        opacity: 0,
        willChange: 'transform, opacity',
        contain: 'layout paint',
      }}
    >
      <button
        type="button"
        className="w-full text-left"
        onPointerEnter={() => onHoverStart(item)}
        onFocus={() => onHoverStart(item)}
        onClick={() => onToggleExpand(item)}
      >
        <header className="flex items-center gap-2">
          <span
            className="inline-flex items-center rounded-md px-2 py-1 text-[11px] font-semibold"
            style={{ background: `${iconColor}26` }}
          >
            <Icon size={12} style={{ color: iconColor }} />
            <span className="ml-1" style={{ color: iconColor }}>
              {typeLabel}
            </span>
          </span>
          <span className="text-xs text-text-secondary">
            {DATE_FORMATTER.format(new Date(item.epochMs))}
          </span>
          <span className="ml-auto inline-flex items-center gap-1 text-xs text-text-tertiary">
            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {isExpanded ? 'Skjul' : 'Vis'}
          </span>
        </header>

        <h2 className="mt-2 text-lg font-semibold text-text-primary">{item.highlight.title}</h2>
        {previewText ? (
          <p
            className={
              isExpanded
                ? 'mt-1 whitespace-pre-wrap text-sm leading-relaxed text-text-secondary'
                : 'mt-1 line-clamp-4 text-sm leading-relaxed text-text-secondary'
            }
          >
            {previewText}
          </p>
        ) : (
          <p className="mt-1 text-sm italic text-text-tertiary">Ingen tekstutdrag tilgjengelig.</p>
        )}
      </button>

      <StoryAttachmentKinds attachments={attachments} />

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.section
            key="expanded-content"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
            className="mt-3 border-t border-white/10 pt-3"
          >
            {isPlaceholder && (
              <p className="mb-2 text-xs text-text-tertiary">Laster full hendelse…</p>
            )}
            {attachments.length > 0 ? (
              <AttachmentList caseId={event.caseId} attachments={attachments} />
            ) : (
              <p className="text-xs italic text-text-tertiary">Ingen vedlegg på denne hendelsen.</p>
            )}
          </motion.section>
        )}
      </AnimatePresence>
    </article>
  );
}

function cardClassName(isExpanded: boolean): string {
  const size = isExpanded ? 'w-[min(760px,94vw)]' : 'w-[min(560px,88vw)]';
  return `pointer-events-auto absolute left-1/2 top-1/2 rounded-xl border border-white/12 bg-surface-raised/95 px-5 py-4 shadow-[0_18px_36px_rgba(0,0,0,0.35)] transition-[width,box-shadow,background-color] duration-300 ease-out ${size}`;
}

function toSeedEvent(item: StoryHighlight): Event {
  return {
    id: item.highlight.eventId as Event['id'],
    caseId: item.highlight.caseId as Event['caseId'],
    timestamp: item.highlight.timestamp,
    type: item.highlight.type,
    source: PLACEHOLDER_SOURCE,
    title: item.highlight.title,
    contentPreview: item.highlight.note,
    metadata: { __storyPlaceholder: true },
    sourceItemId: PLACEHOLDER_ITEM,
  };
}
